/**
 * Categoria: Configurações Globais (Pré-requisitos)
 * Estrutura obrigatória antes de vender, matricular ou avaliar.
 */
export const CONFIGURACOES_GLOBAIS = {
    id: "configuracoes_gerais",
    title: "Configurações Globais (Pré-requisitos)",
    icon: "ph-light ph-gear",
    description: "Estrutura obrigatória que deve ser configurada antes de começar a vender, matricular ou avaliar.",
    steps: [
        {
            id: "config_rh",
            title: "Fase 1 — RH e Estrutura Física",
            description: "A base operacional do seu negócio:",
            subItems: [
                {
                    id: "rh_cargos",
                    icon: "ph-light ph-shield-check",
                    title: "Criar Cargos e Permissões (Roles)",
                    content: `<p class="mb-2"><strong>O que significa:</strong> O sistema de permissões divide quem pode acessar o quê. Antes de convidar um profissional, um cargo (ex: "Professor", "Coordenador") deve existir.</p>
                              <p class="mb-0"><strong>Como cadastrar:</strong> Vá em <em>Administrativo > Cargos</em>, crie o cargo e defina as permissões granulares de acesso.</p>`
                },
                {
                    id: "rh_colab",
                    icon: "ph-light ph-users",
                    title: "Cadastrar Colaboradores",
                    content: `<p class="mb-2"><strong>O que significa:</strong> Os profissionais que vão operar o sistema ou ministrar aulas. Apenas quem tem cargo de instrutor/professor poderá ser listado como dono de uma turma.</p>
                              <p class="mb-0"><strong>Como cadastrar:</strong> Vá ao menu lateral <em>Administrativo > Colaboradores</em>. Clique em "Novo", informe o e-mail (usado no login) e associe o cargo recém-criado.</p>`
                },
                {
                    id: "rh_areas",
                    icon: "ph-light ph-map-pin",
                    title: "Cadastrar Áreas Físicas",
                    content: `<p class="mb-2"><strong>O que significa:</strong> O espaço físico de fato onde a atividade vai ocorrer (ex: "Piscina Infantil", "Piscina 1", "Quadra 2").</p>
                              <p class="mb-0"><strong>Como cadastrar:</strong> Vá ao menu lateral <em>Administrativo > Áreas</em>. Clique em "Nova Área", coloque o nome, cor e descrição.</p>`
                }
            ]
        },
        {
            id: "config_aulas",
            title: "Fase 2 — Catálogo de Aulas e Avaliações",
            description: "Configure o que você ensina, as faixas de horário e as réguas de nota:",
            subItems: [
                {
                    id: "aula_niveis",
                    icon: "ph-light ph-star-half",
                    title: "Cadastrar Níveis de Avaliação",
                    content: `<p class="mb-2"><strong>O que significa:</strong> É a "escala de notas" (ex: "Não alcançado", "Em desenvolvimento", "Excelente").</p>
                              <p class="mb-0"><strong>Como cadastrar:</strong> Vá em <em>Técnico > Níveis</em>. Crie níveis atribuindo <strong>Ordem</strong>. A nota com a MAIOR "ordem" é eleita sempre a "nota máxima" para XP e aprovação.</p>`
                },
                {
                    id: "aula_atividade",
                    icon: "ph-light ph-swimming-pool",
                    title: "Configurar Atividade Curricular",
                    content: `<p class="mb-2"><strong>O que significa:</strong> É a modalidade base (ex: Natação Baby 1).</p>
                              <p class="mb-0"><strong>Como cadastrar:</strong> Vá em <em>Técnico > Atividades</em>. Você pode habilitar a flag <strong>"Sistema de Avaliação"</strong> para liberar o cadastro de Objetivos, Tópicos Fundamentais e XP de Gamificação para esta modalidade.</p>`
                },
                {
                    id: "aula_turmas",
                    icon: "ph-light ph-users-three",
                    title: "Criar Turmas na Grade",
                    content: `<p class="mb-2"><strong>O que significa:</strong> É a união da Atividade, Horários, Área e Professor onde os alunos serão matriculados.</p>
                              <p class="mb-0"><strong>Como cadastrar:</strong> Vá em <em>Administrativo > Turmas</em>. A turma herda automaticamente o sistema de avaliação caso a Atividade correspondente o possua ativado.</p>`
                },
                {
                    id: "aula_eventos",
                    icon: "ph-light ph-calendar-check",
                    title: "Criar Eventos de Avaliação",
                    content: `<p class="mb-2"><strong>O que significa:</strong> É o Ciclo ou "Bimestre" de avaliação (Ex: "Exame 1º Semestre"), com um prazo (Início e Fim). O sistema só permite que os professores deem notas se houver um evento vigente para o dia atual.</p>
                              <p class="mb-2"><strong>Tipos de Evento:</strong></p>
                              <ul class="mb-2">
                                  <li class="mb-1"><strong>Avaliação (Padrão):</strong> Período tradicional de análise técnica e notas.</li>
                                  <li class="mb-1"><strong>Teste:</strong> Provas objetivas que podem adotar limites exatos como <em>Tempo Fixo</em> ou <em>Distância Fixa</em>.</li>
                                  <li class="mb-1"><strong>Extra:</strong> Utilizados em campanhas internas sazonais com foco total em engajamento do cliente, onde os professores focam no ganho de insígnias e XP. Ele é acessado diretamente na página de avaliação pelo professor.</li>
                              </ul>
                              <p class="mb-0"><strong>Como cadastrar:</strong> Vá em <em>Técnico > Eventos</em> e estabeleça as datas e os tipos.</p>`
                },
                {
                    id: "aula_gamificacao",
                    icon: "ph-light ph-game-controller",
                    title: "Configurar Regras de Gamificação e Avanço",
                    content: `<p class="mb-2"><strong>O que significa:</strong> É a página dedicada para estipular as regras irredutíveis para os alunos trocarem de nível com a pontuação recebida.</p>
                              <p class="mb-2">Acessando a página de <em>Gamificação</em>, você informará:</p>
                              <ul class="mb-0">
                                  <li class="mb-1"><strong>Percentual Mínimo Base:</strong> A taxa mínima geral que o aluno precisa atingir do total da sua modalidade.</li>
                                  <li><strong>Tópicos Fundamentais Obrigatórios:</strong> Você pode informar se itens marcados como "Fundamentais" na Atividade são condições retentoras. Ou seja, nesses tópicos, o aluno <strong>só passa se atingir a nota máxima</strong> na escala dos Níveis de Avaliação. São os tópicos <em>core</em> ou essenciais para a segurança ou lógica do esporte.</li>
                              </ul>`
                }
            ]
        },
        {
            id: "config_comercial",
            title: "Fase 3 — Catálogo Comercial",
            description: "Configure o que será vendido para o cliente final:",
            subItems: [
                {
                    id: "com_planos",
                    icon: "ph-light ph-shopping-cart",
                    title: "Tabela de Planos de Venda",
                    content: `<p class="mb-2">Vá em <em>Administrativo > Planos</em> para criar as opções de contração (Mensal, Trimestral). Uma regra fundamental nesta tela é o campo <strong>"Obriga matrícula em horário fixo?"</strong>:</p>
                              <ul>
                                  <li class="mb-1"><span class="badge bg-success">SIM</span>: Garante que os vendedores não conseguirão vender o plano sem selecionar turmas e horários de agendamento na grade, controlando o limite de vagas.</li>
                                  <li><span class="badge bg-danger">NÃO</span>: Ideal para academias de musculação "catraca livre", onde a venda entra, mas o aluno frequenta a hora que quiser (sem reservas em turma).</li>
                              </ul>`
                }
            ]
        }
    ]
};
